import { useState } from "react";

import { ChevronDown } from "lucide-react";

import { cn } from "@/lib/utils";
import type { FreeDailyCredits } from "@/lib/api/credits";

interface CreditDrawerProps {
  freeDaily: FreeDailyCredits;
  /** Open the upgrade modal (credits context). */
  onUpgrade: () => void;
  /** Notify the parent the notice was closed (so it can restore the Tests reopen tab). */
  onDismiss?: () => void;
  className?: string;
}

interface DrawerContent {
  title: string;
  subtitle?: string;
  dotClass: string;
}

/**
 * CreditDrawer — the free-tier credit notice, rendered as a rounded tray tucked
 * BEHIND the composer (see AgentChat: the drawer is a sibling above the composer
 * with a negative bottom margin; the opaque, z-10 composer occludes its lower edge
 * so only this notice peeks out the top). The chatbox itself is unchanged.
 *
 * Dismiss is a small "⌄ Close" tab that rests on the drawer's top edge and only
 * fades in on hover (out of the way otherwise). Upgrade Now is the right-most action.
 *
 * The drawer is the top-most notice: AgentChat suppresses the Preview-Test reopen
 * tab while this is visible (restored on close via onDismiss), so nothing overlaps it.
 *
 * Renders nothing for the healthy state (wall === 'none'); the composer then looks
 * exactly as it does today. Calm neutral surface (not an alarm banner); the only
 * color is a small status dot.
 */
export function CreditDrawer({ freeDaily, onUpgrade, onDismiss, className }: CreditDrawerProps) {
  const [dismissed, setDismissed] = useState(false);

  if (freeDaily.wall === "none" || dismissed) {
    return null;
  }

  const content = getDrawerContent(freeDaily);

  return (
    <div
      className={cn(
        // Tucked behind: sit above the composer, pull it up over our lower edge.
        "group relative z-0 mx-[14px] -mb-[26px] rounded-[22px] border border-border/60 bg-muted/60",
        "px-[18px] pt-3 pb-9",
        className,
      )}
    >
      {/* Close — a small tab resting on the top edge, revealed on hover of the drawer. */}
      <button
        type="button"
        onClick={() => {
          setDismissed(true);
          onDismiss?.();
        }}
        aria-label="Close"
        className={cn(
          "absolute left-1/2 top-0 flex -translate-x-1/2 -translate-y-full items-center gap-1",
          "rounded-t-lg border border-b-0 border-border/60 bg-muted/60 px-2.5 py-0.5 text-[11px] font-medium text-muted-foreground",
          "opacity-0 backdrop-blur-sm transition-opacity duration-150 hover:text-foreground",
          "group-hover:opacity-100 focus-visible:opacity-100",
        )}
      >
        <ChevronDown className="h-3 w-3" />
        Close
      </button>

      <div className="flex items-center gap-3">
        <span className={cn("h-2 w-2 shrink-0 rounded-full", content.dotClass)} />
        <div className="min-w-0 flex-1">
          <p className="text-[13.5px] font-medium leading-tight text-foreground">
            {content.title}
          </p>
          {content.subtitle && (
            <p className="mt-0.5 text-xs text-muted-foreground">{content.subtitle}</p>
          )}
        </div>
        <button
          type="button"
          onClick={onUpgrade}
          className="shrink-0 rounded-[13px] bg-primary px-4 py-2 text-[13px] font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
        >
          Upgrade Now
        </button>
      </div>
    </div>
  );
}

/** Map the server-computed wall to drawer copy + status-dot color. */
function getDrawerContent(freeDaily: FreeDailyCredits): DrawerContent {
  switch (freeDaily.wall) {
    case "today": {
      const at = formatGrantTime(freeDaily.dailyGrantAt);
      return {
        title: "0 free credits remaining today",
        subtitle: at
          ? `You'll get ${freeDaily.dailyGrant} more free credits at ${at}.`
          : `You'll get ${freeDaily.dailyGrant} more free credits tomorrow.`,
        dotClass: "bg-amber-500",
      };
    }
    case "monthly":
      return {
        title: "You've used your free credits this month",
        subtitle: freeDaily.monthResetAt
          ? `Resets ${formatResetDate(freeDaily.monthResetAt)}.`
          : undefined,
        dotClass: "bg-red-500",
      };
    case "lifetime":
      return {
        title: "You've used all your free credits",
        // No reset copy — the lifetime wall is permanent.
        dotClass: "bg-red-500",
      };
    default:
      return { title: "", dotClass: "bg-muted-foreground" };
  }
}

/**
 * Format the next daily-grant instant in the VIEWER's own timezone — the reset is a
 * single global moment (noon Central), so a Central user sees "12:00 PM", London
 * "6:00 PM", etc. `toLocaleTimeString` uses the browser's timezone automatically.
 */
function formatGrantTime(iso?: string): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

function formatResetDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return "next month";
  }
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
